/**
 * AgriPanel — Crop price strip for Sikhio district.
 *
 * Sikhio's agricultural economy: cassava, sugarcane, jasmine rice, field corn.
 * Data from OAE (Office of Agricultural Economics) — reference when no live API.
 */

import type { FallbackTier } from "@sikhio/shared";
import { PanelHeader } from "./PanelHeader";

export interface CropPrice {
  id: string;
  name: string;
  nameEn: string;
  priceThbPerUnit: number;
  unit: string;
  priceTrend: "up" | "down" | "stable";
  weeklyChangePct: number | null;
  season: "in-season" | "off-season" | "harvest";
  areaHectares: number | null;
  source: string;
  observedAt: string;
}

interface Props {
  data: CropPrice[];
  loading: boolean;
  ageMinutes?: number;
  fallbackTier?: FallbackTier;
}

const TREND_SYMBOL = { up: "▲", down: "▼", stable: "─" };
const TREND_COLOR = { up: "var(--good)", down: "var(--bad)", stable: "var(--text-3)" };
const SEASON_COLOR = {
  "in-season": "var(--accent)",
  "harvest": "var(--gold)",
  "off-season": "var(--text-3)",
};
const SEASON_LABEL = { "in-season": "ปลูก", "harvest": "เก็บเกี่ยว", "off-season": "หยุด" };

function formatPrice(price: number, unit: string): string {
  if (unit === "kg") return `${price.toFixed(2)} ฿/กก.`;
  if (unit === "ton") return `${price.toLocaleString()} ฿/ตัน`;
  return `${price.toLocaleString()} ฿`;
}

export function AgriPanel({ data, loading, ageMinutes, fallbackTier }: Props) {
  const crops = data;

  if (loading && crops.length === 0) {
    return (
      <div className="col">
        <div className="eyebrow">เกษตร / AGRICULTURE</div>
        <div className="skeleton" style={{ height: 12, marginTop: 8 }} />
        <div className="skeleton" style={{ height: 12, marginTop: 6, width: "80%" }} />
      </div>
    );
  }
  if (!crops.length) return null;

  return (
    <div className="col" style={{ gap: 4 }}>
      <PanelHeader
        title="เกษตร "
        fallbackTier={fallbackTier}
        ageMinutes={ageMinutes}
      />
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "var(--size-caption)" }}>
        <tbody>
          {crops.map((crop) => (
            <tr key={crop.id} style={{ borderBottom: "1px solid var(--line)" }}>
              <td style={{ padding: "4px 0", color: "var(--text-1)" }}>{crop.name}</td>
              <td style={{ padding: "4px 4px", textAlign: "right", fontFamily: "var(--font-mono)" }}>
                {formatPrice(crop.priceThbPerUnit, crop.unit)}
              </td>
              <td style={{ padding: "4px 0", textAlign: "center", color: TREND_COLOR[crop.priceTrend], fontFamily: "var(--font-mono)", width: 20 }}>
                {TREND_SYMBOL[crop.priceTrend]}
              </td>
              <td style={{ padding: "4px 0", textAlign: "right", color: SEASON_COLOR[crop.season], fontSize: "var(--size-eyebrow)" }}>
                {SEASON_LABEL[crop.season]}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {crops[0]?.source.includes("seed") && (
        <div className="caption" style={{ color: "var(--text-3)", marginTop: 2 }}>
          ราคาอ้างอิง OAE — อัปเดตรายสัปดาห์
        </div>
      )}
    </div>
  );
}
